<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media modern-dropdown"
    right
    ref="dd"
  >
    <template #button-content>
      <div class="notification-bell-wrapper">
        <feather-icon
          :badge="computedCount"
          :badge-classes="['bg-danger', computedCount > 0 ? 'badge-pulse' : '']"
          class="text-body bell-icon"
          icon="BellIcon"
          size="21"
        />
      </div>
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div
        class="dropdown-header d-flex align-items-center py-2 px-2 border-bottom"
      >
        <h4 class="notification-title mb-0 mr-auto font-weight-bolder">
          {{ $t("Notifications") }}
        </h4>
        <div class="d-flex align-items-center">
          <b-badge pill variant="light-primary" class="mr-1 py-50 px-1"
            >{{ computedCount }} {{ $t("New") }}</b-badge
          >
          <b-link
            v-if="computedCount > 0"
            class="mark-all-link font-small-3"
            @click.stop="markAllAsRead"
          >
            {{ $t("Mark all as read") }}
          </b-link>
        </div>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area modern-scroll"
      tagname="li"
    >
      <!-- Empty State -->
      <div v-if="computedData.length == 0" class="p-3 text-center empty-state">
        <feather-icon icon="InboxIcon" size="40" class="text-muted mb-1" />
        <h5 class="text-secondary">{{ $t("All caught up!") }}</h5>
        <p class="text-muted small">
          {{ $t("No new notifications at the moment.") }}
        </p>
      </div>

      <!-- Notification Items -->
      <template v-else>
        <b-link
          v-for="(notification, nIdx) in computedData"
          :key="notificationRowKey(notification, nIdx)"
          class="notification-link"
          @click="handleNotificationClick(notification, nIdx)"
        >
          <b-media
            :class="{ unread: !notification.isRead }"
            class="notification-item py-1 px-2 mb-1"
          >
            <template #aside>
              <b-avatar
                size="40"
                variant="light-primary"
                class="notification-avatar"
              >
                <b-spinner
                  v-if="loadingId === notificationRowKey(notification, nIdx)"
                  small
                />
                <feather-icon
                  v-else
                  :icon="getIcon(notification.title)"
                  size="18"
                />
              </b-avatar>
            </template>
            <div class="d-flex justify-content-between align-items-start mb-25">
              <div class="d-flex align-items-center">
                <span
                  v-if="!notification.isRead"
                  class="unread-dot mr-50"
                ></span>
                <h6
                  class="media-heading mb-0 font-weight-bolder"
                  :class="!notification.isRead ? 'text-primary' : 'text-dark'"
                >
                  {{ notification.title }}
                </h6>
              </div>
              <small class="notification-date text-muted font-small-2">
                {{ formatTime(notification.date) }}
              </small>
            </div>
            <p
              class="notification-text mb-0 font-small-3 line-clamp-2"
              :class="!notification.isRead ? 'text-secondary' : 'text-muted'"
            >
              {{ notification.body }}
            </p>
          </b-media>
        </b-link>
      </template>
    </vue-perfect-scrollbar>

    <!-- Footer -->
    <li class="dropdown-menu-footer p-1">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        block
        class="view-all-btn shadow-sm"
        :to="{ name: 'notification-history' }"
        @click="closeDropdown"
      >
        <feather-icon icon="ExternalLinkIcon" class="mr-50" />
        {{ $t("View All Notifications") }}
      </b-button>
    </li>
  </b-nav-item-dropdown>
</template>

<script>
import {
  BNavItemDropdown,
  BBadge,
  BMedia,
  BLink,
  BAvatar,
  BButton,
  BSpinner,
} from "bootstrap-vue";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { resolveNotificationNavigation } from "@/utils/notificationNavigation";

export default {
  components: {
    BNavItemDropdown,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
    BSpinner,
  },
  directives: {
    Ripple,
  },
  props: {
    data: {
      type: [Object, Array],
      required: true,
    },
  },
  setup() {
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    };

    return {
      perfectScrollbarSettings,
    };
  },
  data() {
    return {
      loadingId: null,
    };
  },
  computed: {
    computedData() {
      let items = [];
      if (Array.isArray(this.data)) items = this.data;
      else if (this.data && Array.isArray(this.data.data))
        items = this.data.data;

      return this.filterNotificationsByRights(items);
    },
    computedCount() {
      const unread = this.computedData.filter((n) => !n.isRead).length;
      if (unread > 0) return unread;
      if (Array.isArray(this.data))
        return this.data.filter((n) => !n.isRead).length;
      if (this.data && typeof this.data.count !== "undefined")
        return this.data.count;
      return 0;
    },
  },
  watch: {
    $route() {
      this.loadingId = null;
    },
    computedCount(newVal, oldVal) {
      if (newVal > oldVal) {
        // Find the newest notification to show in toast
        const newItems = this.computedData.filter((n) => !n.isRead);
        if (newItems.length > 0) {
          const latest = newItems[0];
          this.showNewNotificationToast(latest);
        }
      }
    },
  },
  methods: {
    ...mapActions(["getString"]),
    notificationRowKey(notification, index) {
      const type = notification.type || "n";
      const localID = notification.localID != null ? notification.localID : "";
      const id = notification.id != null ? notification.id : index;
      return `${type}-${localID}-${id}-${index}`;
    },
    isSyntheticNotification(notification) {
      if (!notification) return false;
      const id = Number(notification.id);
      if (!id) return true;
      return String(notification.type || "").toLowerCase() === "inventory";
    },
    filterNotificationsByRights(items) {
      const rights =
        this.$store.state.rights["notification-history"] ||
        this.$store.state.rights["Notifications"];
      if (
        !rights ||
        !rights.notificationTypes ||
        rights.notificationTypes.length === 0
      ) {
        return items;
      }
      const types = rights.notificationTypes;
      return items.filter((n) => this.notificationAllowedForRights(n, types));
    },
    notificationAllowedForRights(n, types) {
      const title = (n.title || "").toLowerCase();
      const type = String(n.type || "").toLowerCase();
      const isInventory = type === "inventory";
      const isChat = type === "chat" || title.includes("message");
      const isStaff = type === "staff" || title.includes("staff");
      const isStudent =
        type === "students" ||
        type === "student" ||
        title.includes("student") ||
        title.includes("marks") ||
        title.includes("fee") ||
        title.includes("attendance");

      if (isInventory && !types.includes("inventory")) return false;
      if (isChat && !types.includes("chat")) return false;
      if (isStaff && !types.includes("staff")) return false;
      if (isStudent && !types.includes("students")) return false;

      return true;
    },
    normalizeNotificationRoute(route) {
      if (!route) return null;
      return String(route).replace(/^\//, "");
    },
    closeDropdown() {
      const elem = this.$refs.dd;
      if (elem) elem.hide();
    },
    showNewNotificationToast(notification) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: notification.title || "New Notification",
          icon: "BellIcon",
          text: notification.body || "You have received a new notification",
          variant: "primary",
        },
      });
    },
    async handleNotificationClick(notification, nIdx = 0) {
      if (this.loadingId) return; // Prevent multiple clicks
      this.loadingId = this.notificationRowKey(notification, nIdx);
      this.closeDropdown();

      if (!notification.isRead && !this.isSyntheticNotification(notification)) {
        try {
          const obj = {
            url: `${this.$store.state.domain}Notifications/MarkAsRead?db=${this.$store.state.userData.db}&id=${notification.id}`,
            token: this.$store.state.userData.token,
          };
          await this.getString(obj);
          notification.isRead = true;
          if (this.data && !Array.isArray(this.data) && this.data.count > 0) {
            this.data.count--;
          }
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      } else if (
        !notification.isRead &&
        this.isSyntheticNotification(notification)
      ) {
        notification.isRead = true;
        if (this.data && !Array.isArray(this.data) && this.data.count > 0) {
          this.data.count--;
        }
      }

      this.navigateFromNotification(notification);
    },
    async markAllAsRead() {
      try {
        const obj = {
          url: `${this.$store.state.domain}Notifications/MarkAllAsRead?db=${this.$store.state.userData.db}&cID=${this.$store.state.userData.cId}`,
          token: this.$store.state.userData.token,
        };
        await this.getString(obj);
        this.computedData.forEach((n) => {
          if (!this.isSyntheticNotification(n)) {
            n.isRead = true;
          }
        });
        if (this.data && !Array.isArray(this.data)) {
          const remaining = this.computedData.filter((n) => !n.isRead).length;
          this.data.count = remaining;
        }
      } catch (error) {
        console.error("Error marking all notifications as read:", error);
      }
    },
    navigateFromNotification(notification) {
      const target = resolveNotificationNavigation(
        notification,
        this.$store.state.rights || {}
      );

      if (!target.ok) {
        this.loadingId = null;
        if (target.reason === "no-rights") {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "No Access",
              icon: "LockIcon",
              text: "You do not have rights to open this record.",
              variant: "warning",
            },
          });
        }
        return;
      }

      const menuParent = (this.$store.state.menu || []).find((el) =>
        (el.children || []).some(
          (ch) => ch.route === target.name || target.aliases?.includes?.(ch.route)
        )
      );
      if (menuParent) {
        this.$store.commit("setCurrentMenu", menuParent);
      }

      this.$router
        .push({
          name: target.name,
          params: target.params || {},
          query: target.query || {},
        })
        .catch(() => {
          this.$router
            .push({ name: target.name, params: target.params || {} })
            .catch(() => {
              this.loadingId = null;
            });
        });
    },
    goTo(route, notification = null) {
      // Back-compat wrapper
      this.navigateFromNotification({
        ...(notification || {}),
        route: route || (notification && notification.route),
      });
    },
    formatTime(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    getIcon(title) {
      const t = (title || "").toLowerCase();
      if (t.includes("inventory")) return "PackageIcon";
      if (t.includes("application") || t.includes("applicant"))
        return "ClipboardIcon";
      if (t.includes("transfer")) return "RepeatIcon";
      if (t.includes("leave")) return "CalendarIcon";
      if (t.includes("staff")) return "UserCheckIcon";
      if (t.includes("student")) return "UsersIcon";
      if (t.includes("fee") || t.includes("payment")) return "DollarSignIcon";
      if (t.includes("exam") || t.includes("marks")) return "FileTextIcon";
      if (t.includes("probation")) return "ClockIcon";
      return "BellIcon";
    },
  },
};
</script>

<style lang="scss" scoped>
.modern-dropdown {
  width: 380px !important;
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
  border-radius: 1rem !important;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.12) !important;
  background: #ffffff;
  margin-top: 15px !important;
  overflow: hidden;

  @media (max-width: 576px) {
    width: 300px !important;
  }
}

.notification-title {
  color: #2c3e50;
  letter-spacing: -0.5px;
}

.modern-scroll {
  max-height: 400px;
  padding: 0.5rem;
}

.notification-link {
  text-decoration: none !important;
  display: block;
}

.notification-item {
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: white;
  border: 1px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(115, 103, 240, 0.08);
    background: #f8f9ff;
    border-color: rgba(115, 103, 240, 0.1);
  }

  &.unread {
    background: rgba(115, 103, 240, 0.05) !important;
    border-color: rgba(115, 103, 240, 0.1) !important;

    .notification-avatar {
      box-shadow: 0 4px 10px rgba(115, 103, 240, 0.2);
    }
  }

  &:not(.unread) {
    .notification-avatar {
      opacity: 0.9;
    }
  }
}

.unread-dot {
  width: 8px;
  height: 8px;
  background-color: #7367f0;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 5px rgba(115, 103, 240, 0.5);
}

.opacity-75 {
  opacity: 0.6;
}

.notification-avatar {
  box-shadow: 0 4px 10px rgba(115, 103, 240, 0.15);
}

.mark-all-link {
  color: #7367f0;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.view-all-btn {
  border-radius: 12px;
  padding: 0.8rem;
  font-weight: 600;
  transition: all 0.2s;
  background-color: rgba(115, 103, 240, 0.05);
}
.notification-date {
  font-size: 0.75rem;
}
</style>

<style></style>
